import React, { useState, useRef, useCallback } from 'react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { NODE_DIMENSIONS, NODE_VISUAL_CONSTANTS } from '@genieartinc/schemas'
import { Icon } from '@/components/ui/icon'
import { useCanvasFiles } from '@/hooks/useCanvasFiles'
import { useParams } from '@tanstack/react-router'
import { readLibraryFileDrop } from '@/components/canvas/nodes/utils/library-file-drop'
import { cn } from '@/lib/utils'
import { toast } from '@/components/ui/toast'
import { UPLOADABLE_AUDIO_ACCEPT, toUploadableAudio, unsupportedAudioMessage } from './audio-file/audio-upload'
import { AudioFileActions } from './audio-file/AudioFileActions'
import { AudioFileEmpty, AudioFileStatus } from './audio-file/AudioFileEmpty'
import { AudioFilePlayer } from './audio-file/AudioFilePlayer'

interface FileMetadata {
  filename?: string
  publicUrl?: string
  contentType?: string
  size?: number
}

interface AudioFileProps {
  id: string
  title?: string
  selected?: boolean
  fileUuid?: string
  canvasId?: string
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onFileChange?: (fileUuid: string, metadata: {
    filename: string
    size: number
    contentType: string
    publicUrl: string
  }) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  hasParent?: boolean
}

function AudioFileComponent({
  id,
  title,
  selected = false,
  canvasId: propCanvasId,
  onSelect,
  onHoverChange,
  onFileChange,
  onStateChange,
  state = {},
  hasParent = false,
}: AudioFileProps) {
  const { id: canvasIdParam } = useParams({ strict: false })
  const canvasId = propCanvasId || (canvasIdParam as string)

  const [isHovered, setIsHovered] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const isUploadingRef = useRef(false)

  const { uploadFile, isUploading, uploadProgress, error } = useCanvasFiles()

  const fileMetadata = state?.fileMetadata as FileMetadata | undefined
  const audioUrl = fileMetadata?.publicUrl || null
  const generationStatus = state?.generationStatus as string | undefined

  const uploadAudioFile = useCallback(async (picked: File) => {
    if (isUploadingRef.current || !canvasId) return
    const file = toUploadableAudio(picked)
    if (!file) {
      toast.error(unsupportedAudioMessage(picked))
      return
    }
    isUploadingRef.current = true

    try {
      const uploadedFile = await uploadFile(canvasId, { file, nodeId: id })
      if (!uploadedFile) {
        throw new Error('Upload failed')
      }

      onFileChange?.(uploadedFile.uuid, {
        filename: uploadedFile.filename,
        size: uploadedFile.size,
        contentType: uploadedFile.contentType,
        publicUrl: uploadedFile.publicUrl || '',
      })
    } catch (err) {
      console.error('Failed to upload audio:', err)
    } finally {
      isUploadingRef.current = false
    }
  }, [canvasId, id, uploadFile, onFileChange])

  const handleAudioUpload = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = UPLOADABLE_AUDIO_ACCEPT

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      await uploadAudioFile(file)
    }

    input.click()
  }, [uploadAudioFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const file = e.dataTransfer.files?.[0]
    if (!file) {
      // A file dragged from the File Manager is already uploaded: link it instead.
      const library = readLibraryFileDrop(e.dataTransfer)
      if (library?.contentType.startsWith('audio/')) onFileChange?.(library.uuid, { filename: library.filename, size: 0, contentType: library.contentType, publicUrl: library.publicUrl })
      return
    }
    if (!file.type.startsWith('audio/')) return

    await uploadAudioFile(file)
  }, [uploadAudioFile, onFileChange])

  const handleAudioRemove = useCallback(() => {
    onFileChange?.('', { filename: '', size: 0, contentType: '', publicUrl: '' })
  }, [onFileChange])

  return (
    <NodeContainer
      nodeId={id}
      title={title || 'Audio'}
      icon="music"
      selected={selected}
      width={NODE_DIMENSIONS.AudioFile.width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent
        className={cn(
          'flex flex-1 flex-col items-stretch overflow-hidden p-1 transition-colors',
          isDragOver && 'bg-primary/10 border-2 border-primary border-dashed',
        )}
        style={{ borderRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.card}px` }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {generationStatus === 'loading' || generationStatus === 'error' ? (
          <AudioFileStatus
            status={generationStatus}
            message={state?.generationMessage as string | undefined}
            progress={state?.generationProgress as number | undefined}
          />
        ) : audioUrl ? (
          <div className="relative flex flex-1 w-full">
            <AudioFilePlayer audioUrl={audioUrl} filename={fileMetadata?.filename} sizeBytes={fileMetadata?.size} />

            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-[10px]">
                <Icon name="loader-circle" className="h-8 w-8 text-white animate-spin" />
              </div>
            )}

            {isHovered && !isUploading && (
              <AudioFileActions
                nodeId={id}
                audioUrl={audioUrl}
                filename={fileMetadata?.filename}
                hasParent={hasParent}
                onReplace={handleAudioUpload}
                onRemove={handleAudioRemove}
              />
            )}
          </div>
        ) : (
          <AudioFileEmpty
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            hasError={!!error}
            onUpload={handleAudioUpload}
          />
        )}
      </CardContent>
    </NodeContainer>
  )
}

export const AudioFile = withNodeOptimization(AudioFileComponent, {
  compareProps: ['id', 'title', 'selected', 'fileUuid', 'canvasId', 'state']
})
